import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Input,
  List,
  message,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Transfer,
  Typography
} from 'antd';
import type { TransferDirection, TransferItem } from 'antd/es/transfer';
import { KeyOutlined, ReloadOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import {
  usePartnerAclPartners,
  usePartnerPermissionCatalog,
  usePartnerPermissions,
  useSetPartnerPermissions
} from 'api/partnerAclAdmin';
import { useScopes } from 'hooks/useScopes';
import { PartnerAclPartner } from 'types/PartnerAclAdmin';
import PartnerAclApplicationPanel from './PartnerAclApplicationPanel';
import PartnerAclClientLinkPanel from './PartnerAclClientLinkPanel';

const PartnerAclAdmin: React.FC = () => {
  const { aclCheck } = useScopes();
  const canRead = aclCheck(['read:partner_acl_management', 'read:site_wide_admin']);
  const canUpdatePermissions = aclCheck(['update:partner_acl_management', 'update:site_wide_admin']);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>();
  const [activeTab, setActiveTab] = useState('permissions');
  const [targetPermissionIds, setTargetPermissionIds] = useState<string[]>([]);
  const [permissionsDirty, setPermissionsDirty] = useState(false);

  const {
    data: partners = [],
    isLoading: partnersLoading,
    refetch: refetchPartners
  } = usePartnerAclPartners({ enabled: canRead });
  const { data: permissionCatalog = [], isLoading: catalogLoading } = usePartnerPermissionCatalog({
    enabled: canRead
  });
  const {
    data: partnerPermissions = [],
    isLoading: partnerPermissionsLoading,
    refetch: refetchPartnerPermissions
  } = usePartnerPermissions(selectedPartnerId, { enabled: canRead && Boolean(selectedPartnerId) });
  const { mutateAsync: savePermissions, isPending: isSavingPermissions } = useSetPartnerPermissions(selectedPartnerId);

  const selectedPartner = useMemo(
    () => partners.find((partner) => partner.id === selectedPartnerId),
    [partners, selectedPartnerId]
  );

  const filteredPartners = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return partners;
    }
    return partners.filter(
      (partner) => partner.name.toLowerCase().includes(query) || partner.id.toLowerCase().includes(query)
    );
  }, [partners, searchQuery]);

  useEffect(() => {
    if (!selectedPartnerId && filteredPartners.length > 0) {
      setSelectedPartnerId(filteredPartners[0].id);
    }
  }, [filteredPartners, selectedPartnerId]);

  useEffect(() => {
    setTargetPermissionIds(partnerPermissions.map((permission) => permission.id));
    setPermissionsDirty(false);
  }, [partnerPermissions, selectedPartnerId]);

  const transferDataSource: TransferItem[] = useMemo(
    () =>
      permissionCatalog.map((permission) => ({
        key: permission.id,
        title: permission.scope,
        description: permission.description ?? undefined
      })),
    [permissionCatalog]
  );

  const handlePermissionChange = (nextTargetKeys: string[], _direction: TransferDirection, _moveKeys: string[]) => {
    setTargetPermissionIds(nextTargetKeys);
    const savedIds = partnerPermissions
      .map((permission) => permission.id)
      .sort()
      .join(',');
    const nextIds = [...nextTargetKeys].sort().join(',');
    setPermissionsDirty(savedIds !== nextIds);
  };

  const handleSavePermissions = async () => {
    if (!selectedPartnerId) {
      return;
    }
    await savePermissions(targetPermissionIds);
    message.success('Partner permissions updated.');
    setPermissionsDirty(false);
  };

  const handleRefresh = async () => {
    await refetchPartners();
    if (selectedPartnerId) {
      await refetchPartnerPermissions();
    }
  };

  const renderPartnerItem = (partner: PartnerAclPartner) => {
    const isSelected = partner.id === selectedPartnerId;
    return (
      <List.Item
        className={`cursor-pointer rounded-lg px-3 ${
          isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
        }`}
        onClick={() => setSelectedPartnerId(partner.id)}
      >
        <List.Item.Meta
          title={
            <Space wrap>
              <Typography.Text strong={isSelected}>{partner.name}</Typography.Text>
              <Tag color={partner.is_active ? 'green' : 'default'}>{partner.is_active ? 'Active' : 'Inactive'}</Tag>
              {partner.auth0_application_client_id && (
                <Tag icon={<KeyOutlined />} color="blue">
                  M2M
                </Tag>
              )}
              {partner.linked_client && <Tag color="purple">Client linked</Tag>}
            </Space>
          }
          description={
            <Typography.Text type="secondary" copyable={{ text: partner.id }} className="text-xs">
              {partner.id}
            </Typography.Text>
          }
        />
      </List.Item>
    );
  };

  if (!canRead) {
    return <Empty description="You do not have permission to manage partner ACL administration." className="py-16" />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" className="mb-4">
        <Col>
          <Typography.Title level={4} className="mb-0">
            Partner ACL Administration
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="mb-0">
            Manage direct partner permissions and Auth0 machine-to-machine credentials.
          </Typography.Paragraph>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={partnersLoading}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={8} xl={7}>
          <Card
            title={
              <Space>
                Partners
                <Badge count={filteredPartners.length} showZero color="#1677ff" />
              </Space>
            }
            className="rounded-xl"
          >
            <Input
              allowClear
              size="large"
              prefix={<SearchOutlined />}
              placeholder="Search by name or partner id"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="mb-4"
            />
            <Spin spinning={partnersLoading}>
              {filteredPartners.length === 0 ? (
                <Empty description="No partners match your search." />
              ) : (
                <List
                  dataSource={filteredPartners}
                  renderItem={renderPartnerItem}
                  className="max-h-[calc(100vh-320px)] overflow-y-auto"
                />
              )}
            </Spin>
          </Card>
        </Col>

        <Col xs={24} lg={16} xl={17}>
          {!selectedPartner ? (
            <Card className="rounded-xl min-h-[420px] flex items-center justify-center">
              <Empty description="Select a partner to manage permissions and credentials." />
            </Card>
          ) : (
            <Card
              className="rounded-xl"
              title={
                <Space direction="vertical" size={0}>
                  <Typography.Text strong className="text-lg">
                    {selectedPartner.name}
                  </Typography.Text>
                  <Typography.Text type="secondary" copyable={{ text: selectedPartner.id }}>
                    Partner ID: {selectedPartner.id}
                  </Typography.Text>
                </Space>
              }
            >
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: 'permissions',
                    label: `Permissions (${targetPermissionIds.length})`,
                    children: (
                      <Spin spinning={catalogLoading || partnerPermissionsLoading}>
                        <Typography.Paragraph type="secondary">
                          Assign direct partner scopes. Saving replaces the full permission set for this partner.
                        </Typography.Paragraph>
                        <Transfer
                          dataSource={transferDataSource}
                          targetKeys={targetPermissionIds}
                          onChange={handlePermissionChange}
                          render={(item) => (
                            <span title={item.description}>
                              <Typography.Text code>{item.title}</Typography.Text>
                              {item.description && (
                                <Typography.Text type="secondary" className="ml-2">
                                  {item.description}
                                </Typography.Text>
                              )}
                            </span>
                          )}
                          titles={['Available permissions', 'Granted permissions']}
                          listStyle={{ width: '100%', height: 360 }}
                          showSearch
                          filterOption={(inputValue, item) =>
                            `${item.title ?? ''} ${item.description ?? ''}`
                              .toLowerCase()
                              .includes(inputValue.toLowerCase())
                          }
                          disabled={!canUpdatePermissions}
                          oneWay={false}
                          className="partner-acl-transfer"
                        />
                        {canUpdatePermissions && (
                          <div className="mt-4 flex justify-end">
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              disabled={!permissionsDirty}
                              loading={isSavingPermissions}
                              onClick={handleSavePermissions}
                            >
                              Save Permissions
                            </Button>
                          </div>
                        )}
                      </Spin>
                    )
                  },
                  {
                    key: 'client',
                    label: 'Linked Client',
                    children: <PartnerAclClientLinkPanel partner={selectedPartner} canUpdate={canUpdatePermissions} />
                  },
                  {
                    key: 'application',
                    label: 'M2M Application',
                    children: <PartnerAclApplicationPanel partner={selectedPartner} />
                  }
                ]}
              />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PartnerAclAdmin;
